// =============================================================================
// PricingAssistantPanel — modal chat assistant on the Plan Builder
//
// Centered modal with a multi-turn chat thread, scoped to ONE plan
// at a time (the currently selected draft in PlanBuilder). The
// agent kicks off the conversation by analysing the plan's current
// price + enabled features and proposing a recommended price with
// a top-3 competitor comparison. The admin can then ask follow-up
// questions ("Why $12.99?", "What if I drop offline mode?",
// "Should annual be steeper?") — the agent has full context of
// previous turns because the client sends the entire conversation
// history each call.
//
// The conversation thread is OWNED BY THE PARENT (PlanBuilder), keyed
// by plan name, so closing + reopening the modal — even for a
// different plan in the same session — restores the prior chat.
// Switching plans while the modal is open swaps in that plan's
// thread (or kicks off a new one if none exists).
//
// When the agent proposes a price it embeds a small fenced
//   ```pricing
//   { "monthly_usd": 12.99, "annual_usd": 119 }
//   ```
// block. The UI strips that out of the rendered message and surfaces
// an inline "Apply to draft" button that calls back into PlanBuilder.
// =============================================================================

import { useEffect, useRef, useState } from 'react';
import { X, Sparkles, Loader2, Check, Send, RefreshCw } from 'lucide-react';
import { cn } from '@/utils/helpers';
import { supabase } from '@/lib/supabase';

const PLATFORM_SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';

// ── Types ────────────────────────────────────────────────────────────

export interface ChatMessage {
  role:    'user' | 'assistant';
  content: string;
}

/** Per-plan context owned by the parent. The parent builds the
 *  `kickoff` markdown string itself (free-form — could be a platform
 *  B2B plan, a tenant B2C plan, anything) and the modal sends it as
 *  the first user message of a fresh thread. Decoupling like this
 *  lets the same modal back both surfaces. */
export interface PricingChatContext {
  /** Stable id used to key the parent's conversations[] map. For B2B
   *  platform plans this is plan.name (e.g. "pro"); for B2C tenant
   *  plans this is the plan.id (UUID). */
  id:           string;
  /** Human label shown in the modal header ("Discussing Pro…"). */
  display_name: string;
  /** Markdown for the first user message on a fresh thread —
   *  including current price, feature list, and the ask. */
  kickoff:      string;
}

interface PricingAssistantPanelProps {
  /** The plan currently being edited. Drives the kickoff context AND
   *  the per-plan conversation key. Null when no plan is selected;
   *  in that case the modal shows an empty-state prompt. */
  context: PricingChatContext | null;
  /** Full conversation thread for `context.id`. Owned by the parent
   *  so it survives modal close/reopen + plan switches. */
  messages: ChatMessage[];
  /** Replace the parent's thread for the current plan. */
  onMessagesChange: (next: ChatMessage[]) => void;
  /** Wipe the thread for the current plan and start fresh. */
  onReset: () => void;
  /** Admin clicked "Apply to draft" on a pricing block. Values are
   *  in CENTS (already converted from the model's USD output) so the
   *  parent can drop them straight into draft.price_per_seat_monthly
   *  and _annual. null means "leave the existing draft value alone"
   *  (Enterprise-style Custom pricing). */
  onApply: (monthlyCents: number | null, annualCents: number | null) => void;
  /** Close the modal. */
  onClose: () => void;
  /** Edge-function path to POST to. Defaults to 'pricing-assistant'
   *  (platform B2B variant); pass 'pricing-assistant-b2c' from the
   *  tenant-admin B2C Plans surface. */
  endpoint?: string;
  /** Extra fields to merge into every request body. The B2C variant
   *  uses this to send { current_tenant_id } so the edge function
   *  can pull the anonymous internal benchmark. */
  extraBody?: Record<string, unknown>;
  /** Short header tagline — defaults to "grounded in market data";
   *  the B2C variant overrides to "anonymous benchmark across
   *  DiscoveryGem partners". */
  taglineText?: string;
}

// ── Pricing-block parsing ───────────────────────────────────────────
// The agent appends a fenced ```pricing block when proposing a
// price. The UI strips it from the rendered message and renders an
// Apply button using the parsed values. Block format:
//
//   ```pricing
//   { "monthly_usd": 12.99, "annual_usd": 119 }
//   ```
//
// Tolerant of whitespace, missing language tag variants, and missing
// fields. If parsing fails the regex match is removed from the
// displayed text anyway — we never show a raw code block to the user.
interface ParsedPricing {
  monthly_usd: number | null;
  annual_usd:  number | null;
}

function extractPricingBlock(text: string): { stripped: string; pricing: ParsedPricing | null } {
  const regex = /```pricing\s*([\s\S]*?)```/i;
  const match = text.match(regex);
  if (!match) return { stripped: text, pricing: null };

  let pricing: ParsedPricing | null = null;
  try {
    const parsed = JSON.parse(match[1].trim());
    pricing = {
      monthly_usd: typeof parsed.monthly_usd === 'number' ? parsed.monthly_usd : null,
      annual_usd:  typeof parsed.annual_usd  === 'number' ? parsed.annual_usd  : null,
    };
  } catch {
    pricing = null;
  }
  return {
    stripped: text.replace(regex, '').trim(),
    pricing,
  };
}

// ── Minimal markdown renderer ───────────────────────────────────────
// We only need: H1/H2/H3 headings, **bold**, `inline code`, bullet
// lists, and paragraph breaks. Pulling in a full markdown library
// here would be overkill — the agent's responses follow a tight
// structure and this 30-line renderer covers every shape we
// instructed it to produce.
function renderMarkdownInline(text: string): React.ReactNode[] {
  // **bold** and `code`. Process bold first so code inside bold still
  // shows as bold (rare but possible).
  const parts: React.ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const token = m[0];
    if (token.startsWith('**')) {
      parts.push(<strong key={`b-${key++}`} className="font-semibold text-surface-900">{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`')) {
      parts.push(<code key={`c-${key++}`} className="px-1 py-0.5 rounded bg-overlay/[0.05] text-[11px] font-mono">{token.slice(1, -1)}</code>);
    }
    last = m.index + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function MarkdownBlock({ text }: { text: string }) {
  const lines = text.split('\n');
  const blocks: React.ReactNode[] = [];
  let buf: string[] = [];
  let bulletBuf: string[] = [];

  const flushParagraph = () => {
    if (buf.length === 0) return;
    blocks.push(
      <p key={`p-${blocks.length}`} className="text-[12.5px] text-surface-700 leading-relaxed">
        {renderMarkdownInline(buf.join(' '))}
      </p>,
    );
    buf = [];
  };
  const flushBullets = () => {
    if (bulletBuf.length === 0) return;
    blocks.push(
      <ul key={`ul-${blocks.length}`} className="list-disc pl-5 space-y-0.5 text-[12.5px] text-surface-700 leading-relaxed marker:text-surface-400">
        {bulletBuf.map((b, i) => <li key={i}>{renderMarkdownInline(b)}</li>)}
      </ul>,
    );
    bulletBuf = [];
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.startsWith('### ')) {
      flushParagraph(); flushBullets();
      blocks.push(<h3 key={`h3-${blocks.length}`} className="text-[12px] font-bold uppercase tracking-wider text-surface-500 mt-2.5 first:mt-0">{line.slice(4)}</h3>);
    } else if (line.startsWith('## ')) {
      flushParagraph(); flushBullets();
      blocks.push(<h2 key={`h2-${blocks.length}`} className="text-[13px] font-bold text-surface-900 mt-3 first:mt-0">{line.slice(3)}</h2>);
    } else if (line.startsWith('# ')) {
      flushParagraph(); flushBullets();
      blocks.push(<h1 key={`h1-${blocks.length}`} className="text-sm font-bold text-surface-900 mt-3 first:mt-0">{line.slice(2)}</h1>);
    } else if (/^[-*]\s+/.test(line)) {
      flushParagraph();
      bulletBuf.push(line.replace(/^[-*]\s+/, ''));
    } else if (line === '') {
      flushParagraph(); flushBullets();
    } else {
      flushBullets();
      buf.push(line);
    }
  }
  flushParagraph(); flushBullets();
  return <div className="space-y-1.5">{blocks}</div>;
}

// ── Component ───────────────────────────────────────────────────────

export function PricingAssistantPanel({
  context, messages, onMessagesChange, onReset, onApply, onClose,
  endpoint    = 'pricing-assistant',
  extraBody   = {},
  taglineText = 'grounded in market data',
}: PricingAssistantPanelProps) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justApplied, setJustApplied] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll to bottom on new message / loader toggle
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, loading]);

  // Auto-fire the kickoff message when the modal opens against a
  // plan with no existing conversation. The parent built the
  // kickoff string (so platform B2B vs tenant B2C can frame their
  // own first message) — we just send it.
  useEffect(() => {
    if (!context) return;
    if (messages.length > 0) return; // existing thread — don't re-kick
    if (loading) return;

    void sendMessage([{ role: 'user', content: context.kickoff }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context?.id]);

  /**
   * Send a turn. `displayMessages` is the full conversation history
   * we want to render + post to the edge function (already includes
   * the just-appended user turn).
   */
  async function sendMessage(displayMessages: ChatMessage[]) {
    if (!context) return;
    setLoading(true);
    setError(null);
    onMessagesChange(displayMessages);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const res = await fetch(`${PLATFORM_SUPABASE_URL}/functions/v1/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ messages: displayMessages, ...extraBody }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `Pricing assistant returned ${res.status}`);

      onMessagesChange([
        ...displayMessages,
        { role: 'assistant', content: json.reply ?? '' },
      ]);
    } catch (e) {
      console.error('[PricingAssistantPanel] failed:', e);
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || loading) return;
    setInput('');
    void sendMessage([...messages, { role: 'user', content: trimmed }]);
  };

  const handleApply = (pricing: ParsedPricing) => {
    const monthlyCents = pricing.monthly_usd === null ? null : Math.round(pricing.monthly_usd * 100);
    const annualCents  = pricing.annual_usd  === null ? null : Math.round(pricing.annual_usd  * 100);
    onApply(monthlyCents, annualCents);
    setJustApplied(true);
    setTimeout(() => setJustApplied(false), 2400);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" />

      <div
        className="relative w-full max-w-2xl max-h-[85vh] bg-surface-0 dark:bg-surface-100 rounded-2xl shadow-2xl border border-overlay/[0.08] flex flex-col overflow-hidden animate-scale-in"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="pricing-chat-title"
        aria-modal="true"
      >
        {/* ── Header ───────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-overlay/[0.06]">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center shrink-0">
              <Sparkles size={15} className="text-white" />
            </div>
            <div className="min-w-0">
              <h2 id="pricing-chat-title" className="text-base font-bold text-surface-900 truncate">
                Pricing Assistant
              </h2>
              <p className="text-[11px] text-surface-500 truncate">
                {context ? <>Discussing <span className="font-semibold text-surface-700">{context.display_name}</span> · {taglineText}</>
                         : 'Select a plan to start'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {messages.length > 0 && (
              <button
                onClick={onReset}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium text-surface-500 hover:text-surface-800 hover:bg-overlay/[0.04] transition-colors"
                title="Start a fresh conversation for this plan"
              >
                <RefreshCw size={11} /> New chat
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-surface-500 hover:bg-overlay/[0.04] transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* ── Chat thread (scrollable) ───────────────────────────
            scrollbar-elegant swaps the default chunky gray scrollbar
            for a slim brand-teal pill that fades up on hover.
            Utility defined in src/styles/globals.css. */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-elegant px-5 py-4 space-y-4">
          {!context && (
            <div className="text-center text-xs text-surface-500 py-12">
              Select a plan to start a pricing conversation.
            </div>
          )}

          {context && messages.length === 0 && !loading && (
            <div className="text-center text-xs text-surface-500 py-8">
              Preparing the kickoff analysis for <span className="font-semibold text-surface-700">{context.display_name}</span>…
            </div>
          )}

          {messages.map((msg, i) => {
            if (msg.role === 'user') {
              return (
                <div key={i} className="flex justify-end">
                  <div className="max-w-[85%] px-3.5 py-2 rounded-2xl rounded-tr-sm bg-brand-500 text-white text-[12.5px] leading-relaxed whitespace-pre-wrap">
                    {msg.content}
                  </div>
                </div>
              );
            }
            const { stripped, pricing } = extractPricingBlock(msg.content);
            return (
              <div key={i} className="flex justify-start">
                <div className="max-w-[92%] px-3.5 py-2.5 rounded-2xl rounded-tl-sm bg-overlay/[0.03] dark:bg-overlay/[0.05] border border-overlay/[0.05]">
                  <MarkdownBlock text={stripped} />

                  {pricing && (pricing.monthly_usd !== null || pricing.annual_usd !== null) && (
                    <div className="mt-3 pt-3 border-t border-overlay/[0.06] flex items-center justify-between gap-3 flex-wrap">
                      <div className="text-[11px] text-surface-600">
                        <span className="font-semibold text-surface-900 tabular-nums">
                          {pricing.monthly_usd === null ? 'Custom' : `$${pricing.monthly_usd.toFixed(2)}/mo`}
                        </span>
                        {pricing.annual_usd !== null && pricing.annual_usd > 0 && (
                          <span className="text-surface-500"> · ${pricing.annual_usd.toFixed(0)}/yr</span>
                        )}
                      </div>
                      <button
                        onClick={() => handleApply(pricing)}
                        className={cn(
                          'inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-semibold transition-colors',
                          justApplied
                            ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
                            : 'bg-brand-500 text-white hover:bg-brand-600',
                        )}
                      >
                        {justApplied ? <><Check size={12} /> Applied</> : 'Apply to draft'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {loading && (
            <div className="flex justify-start">
              <div className="px-3.5 py-2.5 rounded-2xl rounded-tl-sm bg-overlay/[0.03] border border-overlay/[0.05] inline-flex items-center gap-2 text-[12px] text-surface-500">
                <Loader2 size={13} className="animate-spin" /> Reading market data…
              </div>
            </div>
          )}

          {error && (
            <div className="px-3 py-2.5 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-xs text-red-700 dark:text-red-300">
              {error}
            </div>
          )}
        </div>

        {/* ── Composer ─────────────────────────────────────────── */}
        <form onSubmit={handleSubmit} className="border-t border-overlay/[0.06] px-3 py-2.5 flex items-end gap-2">
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e as any);
              }
            }}
            rows={1}
            placeholder={context ? 'Ask a follow-up — "Why $12.99?", "What about $19.99?", "What if I drop offline mode?"' : 'Select a plan first…'}
            disabled={!context || loading}
            className="flex-1 resize-none px-3 py-2 text-sm bg-surface-50/40 dark:bg-surface-200/30 border border-overlay/[0.08] rounded-lg text-surface-900 placeholder-surface-400 focus:outline-none focus:border-brand-500/40 focus:ring-2 focus:ring-brand-500/20 max-h-32 scrollbar-elegant"
          />
          <button
            type="submit"
            disabled={!context || loading || !input.trim()}
            className={cn(
              'p-2.5 rounded-lg transition-colors',
              !context || loading || !input.trim()
                ? 'bg-overlay/[0.06] text-surface-400 cursor-not-allowed'
                : 'bg-brand-500 text-white hover:bg-brand-600',
            )}
            aria-label="Send"
          >
            <Send size={14} />
          </button>
        </form>
      </div>
    </div>
  );
}
// (Kickoff message builders live in the parent components since
//  each surface — platform B2B vs tenant B2C — needs different
//  context. See PlanBuilder.tsx and B2CPlans.tsx.)
